import { Plus, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { ENUM_LABELS, type PatientDetail, type PaymentRow, formatPersonName } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import type { Column } from '#client/components/ui/DataTable';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Modal } from '#client/components/ui/Modal';
import { NumberField } from '#client/components/ui/NumberField';
import { Select } from '#client/components/ui/Select';
import { TextArea } from '#client/components/ui/TextArea';
import { useCan } from '#client/hooks/useCan';
import { useFormValues, type SetField } from '#client/hooks/useFormValues';
import { useMutation } from '#client/hooks/useMutation';
import { formatDateTime, formatMoney, dash } from '#client/lib/format';
import { useToast } from '#client/providers/ToastProvider';
import {
  type PaymentDraft,
  type PaymentFieldErrors,
  type PaymentMode,
  buildPaymentBody,
  initialPaymentDraft,
  readPaymentFieldErrors,
} from './payment.logic';
import { recordPayment } from './payment.service';
import { PAYMENT_METHOD_OPTIONS } from '#client/lib/form-choices';

const columns: Column<PaymentRow>[] = [
  {
    key: 'taken_at',
    header: 'When',
    width: '200px',
    render: (row) => formatDateTime(row.taken_at),
  },
  {
    key: 'amount',
    header: 'Amount',
    width: '140px',
    align: 'end',
    render: (row) => (
      <span className={row.amount < 0 ? 'tnum c-money--due' : 'tnum'}>
        {formatMoney(row.amount)}
      </span>
    ),
  },
  {
    key: 'method',
    header: 'Method',
    width: '150px',
    render: (row) => (row.method ? ENUM_LABELS.paymentMethod[row.method] : 'Not recorded'),
  },
  {
    key: 'taken_by',
    header: 'Taken by',
    width: '180px',
    render: (row) => formatPersonName(row.taken_by),
  },
  { key: 'note', header: 'Note', render: (row) => dash(row.note) },
];

/**
 * The ledger behind `paid`: every amount taken or handed back, by whom.
 *
 * The balance collected when a patient returns for the report is recorded
 * here rather than by editing the visit, so it is credited to whoever is at
 * the desk that day — which is what makes their Day Close add up.
 */
export function PatientPayments({
  patient,
  onChanged,
}: {
  patient: PatientDetail;
  onChanged: () => void;
}) {
  const can = useCan();
  const [mode, setMode] = useState<PaymentMode | null>(null);
  const takesMoney = can('patient.payment');

  return (
    <Card
      title="Payments"
      flush
      actions={
        takesMoney && (
          <>
            {patient.paid > 0 && (
              <Button size="sm" icon={<Undo2 />} onClick={() => setMode('refund')}>
                Refund
              </Button>
            )}
            <Button size="sm" icon={<Plus />} onClick={() => setMode('payment')}>
              Record payment
            </Button>
          </>
        )
      }
    >
      {patient.payments.length === 0 ? (
        <EmptyState title="Nothing paid yet" body="Payments against this visit appear here." />
      ) : (
        <DataTable
          columns={columns}
          rows={patient.payments}
          rowKey={(row) => row.id}
          caption="Payments and refunds against this visit"
        />
      )}
      {mode && (
        <PaymentModal
          mode={mode}
          patient={patient}
          onClose={() => setMode(null)}
          onSaved={() => {
            setMode(null);
            onChanged();
          }}
        />
      )}
    </Card>
  );
}

interface PaymentModalProps {
  mode: PaymentMode;
  patient: PatientDetail;
  onClose: () => void;
  onSaved: () => void;
}

function PaymentModal({ mode, patient, onClose, onSaved }: PaymentModalProps) {
  const toast = useToast();
  const isRefund = mode === 'refund';
  const { values, set } = useFormValues(initialPaymentDraft(mode, patient));
  const payment = useMutation('That did not go through.');
  // Null unless the server rejected specific fields; then those messages sit
  // under their inputs instead of in the form-level banner.
  const [fieldErrors, setFieldErrors] = useState<PaymentFieldErrors | null>(null);

  async function handleSave() {
    setFieldErrors(null);
    const outcome = await payment.run(() =>
      recordPayment(patient.id, buildPaymentBody(mode, values)).catch((caught: unknown) => {
        setFieldErrors(readPaymentFieldErrors(caught));
        throw caught;
      }),
    );
    if (!outcome.ok) return;
    toast.success(isRefund ? 'Refund recorded' : 'Payment recorded');
    onSaved();
  }

  const formError = fieldErrors ? null : payment.errorMessage;

  return (
    <Modal
      open
      title={isRefund ? 'Refund' : 'Record payment'}
      onClose={payment.isPending ? () => undefined : onClose}
      primaryAction={{
        label: isRefund ? 'Record refund' : 'Record payment',
        onAction: () => void handleSave(),
        loading: payment.isPending,
        destructive: isRefund,
      }}
      secondaryAction={{ label: 'Cancel', onAction: onClose }}
    >
      <div className="c-form-stack">
        <p>
          {isRefund
            ? `${formatMoney(patient.paid)} has been paid against this visit.`
            : `${formatMoney(patient.balance)} is outstanding on this visit.`}
        </p>
        <PaymentFields
          isRefund={isRefund}
          values={values}
          onFieldChange={set}
          errors={fieldErrors ?? {}}
        />
        {formError && <Banner tone="critical">{formError}</Banner>}
      </div>
    </Modal>
  );
}

interface PaymentFieldsProps {
  isRefund: boolean;
  values: PaymentDraft;
  onFieldChange: SetField<PaymentDraft>;
  errors: PaymentFieldErrors;
}

function PaymentFields({ isRefund, values, onFieldChange, errors }: PaymentFieldsProps) {
  return (
    <>
      <NumberField
        label="Amount"
        min={0}
        step={0.01}
        prefix="Rs"
        value={values.amount}
        error={errors.amount}
        onChange={(amount) => onFieldChange('amount', amount)}
        required
      />
      <Select
        label={isRefund ? 'Refunded by' : 'Payment method'}
        options={PAYMENT_METHOD_OPTIONS}
        placeholder="Choose a method"
        value={values.method}
        error={errors.method}
        onChange={(choice) => onFieldChange('method', choice)}
        required
      />
      <TextArea
        label={isRefund ? 'Reason' : 'Note'}
        value={values.note}
        error={errors.note}
        onChange={(event) => onFieldChange('note', event.target.value)}
        required={isRefund}
        helpText={
          isRefund ? 'Required — a refund is the row the drawer count is asked about.' : undefined
        }
      />
    </>
  );
}
